import { appNoOp } from '@actions/app.action';
import {
  extrasRemoveExtrasBundle,
  extrasSellExtrasBundle,
  extrasSellExtrasBundleSuccess,
  extrasSellExtrasSsrs,
  extrasSellExtrasSsrsSuccess,
} from '@actions/extras.action';
import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { selectExtrasTotalSelectionsState } from '@selectors/extras.selector';
import { SsrService } from '@services/api-clients/ssr.service';
import { Observable, of } from 'rxjs';
import { map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { selectAvailabilitySelectedState } from "@selectors/availability.selector";
import { CabinClassWithExtrasBundle, CabinClassWithoutExtrasBundle } from "@constants/cabin.constants";
import { TripService } from "@services/api-clients/trip.service";
import { SellBundleRequest } from "@models/requests/sell-bundle-request.model";
import { N0SellKey } from "@models/requests/trip-sell-request.model";
import { selectIsExtrasPage, selectRouteParam } from "@selectors/router.selector";
import { CULTURE_ROUTE_PARAM, FALLBACK_CULTURE } from "@constants/app.constants";

@Injectable()
export class ExtrasEffect {
  sellExtrasSsrs$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(extrasSellExtrasSsrs),
      concatLatestFrom(() =>
        [
          this.store.select(selectExtrasTotalSelectionsState),
          this.store.select(selectIsExtrasPage),
          this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)),
        ]
      ),
      mergeMap(([action, selection, isExtrasPage, cultureCode]) => {
        if (selection) {
          return this.ssrService
            .ssrUpdate(selection,  isExtrasPage, cultureCode ?? FALLBACK_CULTURE)
            .pipe(map(() => extrasSellExtrasSsrsSuccess({redirectToPayment: action.redirectToPayment})));
        } else {
          return of(appNoOp());
        }
      }),
    );
  });

  sellExtrasBundle$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(extrasSellExtrasBundle),
      withLatestFrom(this.store.select(selectAvailabilitySelectedState)),
      mergeMap(([, selection]) => {
        if (!selection || !selection.sellKeys[0]?.bundleCode) {
          return of(appNoOp());
        }

        const request: SellBundleRequest = {
          keys: selection.sellKeys
            .filter((key): key is N0SellKey => key !== null)
            .map((key, index) => index === 0
              ? {
                ...key,
                bundleCode: CabinClassWithExtrasBundle[key.bundleCode!]
              }
              : key
            ),
        };

        return this.tripService
          .sellBundle(request)
          .pipe(
            map((response) => {
              return extrasSellExtrasBundleSuccess({payload: response.data, request: request})
            })
          );
      }),
    )
  });

  removeExtrasBundle$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(extrasRemoveExtrasBundle),
      withLatestFrom(this.store.select(selectAvailabilitySelectedState)),
      mergeMap(([, selection]) => {
        if (!selection || !selection.sellKeys[0]?.bundleCode) {
          return of(appNoOp());
        }

        const request: SellBundleRequest = {
          keys: selection.sellKeys
            .filter((key): key is N0SellKey => key !== null)
            .map((key, index) => index === 0
              ? {
                ...key,
                bundleCode: CabinClassWithoutExtrasBundle[key.bundleCode!]
              }
              : key
            ),
        };

        return this.tripService
          .sellBundle(request)
          .pipe(
            map((response) => {
              return extrasSellExtrasBundleSuccess({payload: response.data, request: request});
            })
          );
      }),
    )
  });

  constructor(
    private actions$: Actions,
    private store: Store,
    private ssrService: SsrService,
    private tripService: TripService,
  ) {}
}
